import { useState } from "react";
import type { ProductLibrary } from "@/api/product-library";
import { assetsApi } from "@/api/assets";

/** Teto de referências que o gerador aceita. Gemini (Nano Banana) suporta até
 *  14; mantemos folga em 10 pra não inflar o request. */
const MAX_REFS = 10;

type PickerItem = {
  id: string;
  contentPath: string | null;
  kind: "person" | "creative" | "upload";
  title: string;
  subtitle: string;
};

/**
 * Modal de seleção de reference images antes de gerar arte.
 *
 * Aparece quando:
 *  - User aprovou copy de post/carrossel/stories
 *  - Próxima etapa é um template *_arte (direct_api)
 *  - Issue tem productId vinculado E o produto tem library com ≥1 asset
 *
 * Além da brand library do produto, o user pode enviar imagens do próprio
 * computador (viram assets da company e entram como referência desta geração).
 *
 * Segue o mesmo padrão visual dos outros modais (VariationSelectionModal,
 * NextStepModal): overlay rgba navy + card var(--wf-paper) com header/footer
 * delimitados, botões wf-btn. Sem inline styling fora de padrão.
 */
export function ReferenceImagePickerModal({
  library,
  companyId,
  isPending,
  onCancel,
  onConfirm,
}: {
  library: ProductLibrary;
  companyId: string;
  isPending: boolean;
  onCancel: () => void;
  onConfirm: (assetIds: string[]) => void;
}) {
  const libraryItems: PickerItem[] = [
    ...library.peopleLibrary.map((p) => ({
      id: p.attachmentId,
      contentPath: p.contentPath,
      kind: "person" as const,
      title: p.expertName ?? "Foto do expert",
      subtitle: p.context ?? "",
    })),
    ...library.creativeLibrary.map((c) => ({
      id: c.attachmentId,
      contentPath: c.contentPath,
      kind: "creative" as const,
      title: c.category.replace(/_/g, " "),
      subtitle: c.notes ?? "",
    })),
  ];

  // Imagens enviadas do computador nesta sessão (assets da company). Ficam na
  // frente da lista pra ficarem visíveis logo após o upload.
  const [uploaded, setUploaded] = useState<PickerItem[]>([]);
  const [uploading, setUploading] = useState(false);
  const [uploadError, setUploadError] = useState<string | null>(null);

  const allItems: PickerItem[] = [...uploaded, ...libraryItems];

  // Pré-seleção: todas as people + 2 primeiros creatives, max 4 total
  const initialSelected = (() => {
    const selected: string[] = [];
    for (const p of library.peopleLibrary) {
      if (selected.length >= 4) break;
      selected.push(p.attachmentId);
    }
    for (const c of library.creativeLibrary.slice(0, 2)) {
      if (selected.length >= 4) break;
      selected.push(c.attachmentId);
    }
    return selected;
  })();

  const [selected, setSelected] = useState<string[]>(initialSelected);
  const max = Math.min(allItems.length, MAX_REFS);

  const toggle = (id: string) => {
    setSelected((prev) => {
      if (prev.includes(id)) return prev.filter((x) => x !== id);
      if (prev.length >= MAX_REFS) return prev;
      return [...prev, id];
    });
  };

  const handleUpload = async (files: FileList | null) => {
    if (!files || files.length === 0) return;
    setUploadError(null);
    setUploading(true);
    try {
      for (const file of Array.from(files)) {
        const previewUrl = URL.createObjectURL(file);
        const res = await assetsApi.uploadImage(companyId, file);
        const item: PickerItem = {
          id: res.assetId,
          contentPath: previewUrl,
          kind: "upload",
          title: res.originalFilename ?? file.name,
          subtitle: "Enviada do computador",
        };
        setUploaded((prev) => [item, ...prev]);
        // Auto-seleciona a recém-enviada se ainda couber no teto.
        setSelected((prev) =>
          prev.length < MAX_REFS ? [res.assetId, ...prev] : prev,
        );
      }
    } catch (e) {
      setUploadError((e as Error).message || "Falha ao enviar a imagem");
    } finally {
      setUploading(false);
    }
  };

  return (
    <div
      role="dialog"
      aria-modal="true"
      style={{
        position: "fixed",
        inset: 0,
        zIndex: 200,
        background: "rgba(13,21,38,0.55)",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        padding: 20,
      }}
      onClick={(e) => {
        if (e.target === e.currentTarget && !isPending) onCancel();
      }}
    >
      <div
        style={{
          width: "min(680px, 100%)",
          maxHeight: "85vh",
          background: "var(--wf-paper)",
          borderRadius: 14,
          border: "1px solid var(--wf-ink-5)",
          display: "flex",
          flexDirection: "column",
          overflow: "hidden",
          fontFamily: "var(--wf-font-body)",
        }}
      >
        <header
          style={{
            padding: "16px 20px",
            borderBottom: "1px solid var(--wf-ink-5)",
          }}
        >
          <h2
            className="wf-display"
            style={{
              fontSize: 18,
              fontWeight: 700,
              letterSpacing: "-0.01em",
              margin: 0,
              marginBottom: 4,
            }}
          >
            Referências para a arte
          </h2>
          <p
            style={{
              fontSize: 12,
              color: "var(--wf-ink-3)",
              lineHeight: 1.5,
              margin: 0,
            }}
          >
            Brand library de <strong>{library.productName}</strong>. Marque até{" "}
            {MAX_REFS} imagens que o gerador deve usar como referência — fotos do
            expert e criativos antigos pra manter a identidade — ou envie imagens
            do seu computador. Pré-selecionei {initialSelected.length} comuns;
            ajuste como quiser.
          </p>
        </header>

        <div
          style={{
            overflowY: "auto",
            padding: 16,
            display: "grid",
            gridTemplateColumns: "repeat(auto-fill, minmax(150px, 1fr))",
            gap: 12,
          }}
        >
          <label
            style={{
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              justifyContent: "center",
              gap: 6,
              aspectRatio: "1 / 1",
              background: "var(--wf-bg)",
              border: "1px dashed var(--nd-orange)",
              borderRadius: 10,
              cursor: uploading || isPending ? "default" : "pointer",
              color: "var(--nd-orange)",
              fontSize: 12,
              fontWeight: 600,
              textAlign: "center",
              padding: 10,
              opacity: uploading || isPending ? 0.6 : 1,
            }}
          >
            <input
              type="file"
              accept="image/*"
              multiple
              hidden
              disabled={uploading || isPending}
              onChange={(e) => {
                void handleUpload(e.target.files);
                e.target.value = "";
              }}
            />
            <span style={{ fontSize: 22, lineHeight: 1 }}>＋</span>
            <span>{uploading ? "enviando…" : "Enviar do computador"}</span>
          </label>

          {allItems.map((item) => {
            const checked = selected.includes(item.id);
            return (
              <button
                key={item.id}
                type="button"
                onClick={() => toggle(item.id)}
                style={{
                  textAlign: "left",
                  padding: 0,
                  background: checked ? "rgba(255,112,0,0.06)" : "var(--wf-bg)",
                  border: checked
                    ? "1px solid var(--nd-orange)"
                    : "1px solid var(--wf-ink-5)",
                  borderRadius: 10,
                  cursor: "pointer",
                  fontFamily: "inherit",
                  color: "inherit",
                  overflow: "hidden",
                  position: "relative",
                  transition: "border-color 120ms",
                }}
              >
                {item.contentPath ? (
                  <img
                    src={item.contentPath}
                    alt={item.title}
                    style={{
                      width: "100%",
                      aspectRatio: "1 / 1",
                      objectFit: "cover",
                      display: "block",
                    }}
                  />
                ) : (
                  <div
                    style={{
                      width: "100%",
                      aspectRatio: "1 / 1",
                      background: "var(--wf-ink-5)",
                    }}
                  />
                )}
                <div style={{ padding: "8px 10px" }}>
                  <span
                    className={`wf-pill ${item.kind === "creative" ? "wf-pill--gray" : "wf-pill--orange"}`}
                    style={{ fontSize: 10, padding: "1px 7px" }}
                  >
                    {item.kind === "person"
                      ? "Pessoa"
                      : item.kind === "upload"
                        ? "Enviada"
                        : "Criativo"}
                  </span>
                  <div
                    style={{
                      fontSize: 12,
                      color: "var(--wf-ink-1)",
                      lineHeight: 1.3,
                      marginTop: 5,
                      whiteSpace: "nowrap",
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                    }}
                  >
                    {item.title}
                  </div>
                  {item.subtitle && (
                    <div
                      style={{
                        fontSize: 11,
                        color: "var(--wf-ink-3)",
                        lineHeight: 1.3,
                        whiteSpace: "nowrap",
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                      }}
                    >
                      {item.subtitle}
                    </div>
                  )}
                </div>
                {checked && (
                  <div
                    style={{
                      position: "absolute",
                      top: 6,
                      right: 6,
                      background: "var(--nd-orange)",
                      color: "white",
                      width: 20,
                      height: 20,
                      borderRadius: "50%",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      fontSize: 12,
                      fontWeight: 700,
                    }}
                  >
                    ✓
                  </div>
                )}
              </button>
            );
          })}
        </div>

        {uploadError && (
          <div
            style={{
              padding: "0 20px 4px",
              fontSize: 12,
              color: "var(--nd-orange)",
            }}
          >
            {uploadError}
          </div>
        )}

        <footer
          style={{
            padding: "12px 20px",
            borderTop: "1px solid var(--wf-ink-5)",
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            gap: 10,
          }}
        >
          <span style={{ fontSize: 12, color: "var(--wf-ink-3)" }}>
            {selected.length === 0
              ? "Referência é opcional — pode gerar sem"
              : `${selected.length} de ${max} selecionadas`}
          </span>
          <div style={{ display: "flex", gap: 8 }}>
            <button
              type="button"
              onClick={onCancel}
              disabled={isPending}
              className="wf-btn wf-btn--ghost wf-btn--sm"
            >
              Cancelar
            </button>
            <button
              type="button"
              onClick={() => onConfirm(selected)}
              disabled={isPending}
              className="wf-btn wf-btn--primary wf-btn--sm"
            >
              {isPending
                ? "gerando…"
                : selected.length === 0
                  ? "Gerar arte sem referência"
                  : `Gerar arte com ${selected.length} ref${selected.length === 1 ? "" : "s"}`}
            </button>
          </div>
        </footer>
      </div>
    </div>
  );
}
